import { createPortal } from "react-dom";
import { PillLabel } from "@/components/ui/pill-label";
import { MobileCalendarStream } from "./MobileCalendarStream";
import {
  periodStart,
  periodEnd,
  shiftPeriod,
  streamRadius,
} from "./calendarStreamModel";
import { MobileCreateAction } from "../layout/MobileCreateAction";
import { useAnimatedSelection } from "@/hooks/use-animated-selection";
import { AgendaSection } from "./AgendaSection";
import MobileHeader from "../layout/MobileHeader";
import { useCalendarTasks } from "./useCalendarTasks";
import {
  useCallback,
  useEffect,
  useRef,
  useMemo,
  useState,
  type DragEvent,
  type CSSProperties,
} from "react";
import "./calendar.css";
import { CalendarEvent } from "./CalendarEvent";
import {
  useGetIdentity,
  useGetList,
  useGetMany,
  useNotify,
  useStore,
  useUpdate,
  type Identifier,
} from "ra-core";
import {
  addDays,
  addMonths,
  addYears,
  endOfMonth,
  endOfWeek,
  endOfYear,
  format,
  isSameMonth,
  isToday,
  parseISO,
  startOfMonth,
  startOfWeek,
  startOfYear,
} from "date-fns";
import { ru } from "date-fns/locale";
import {
  ArrowLeft,
  CalendarDays,
  Check,
  ChevronLeft,
  ChevronRight,
  MoreHorizontal,
  RefreshCw,
  SlidersHorizontal,
  X,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { SegmentedControl } from "@/components/ui/segmented-control";
import { useIsMobile } from "@/hooks/use-mobile";
import type { Sale, Task } from "../types";
import { isInstallation, installationOnDay } from "./installation";
import {
  monthWeeks,
  tasksOnDay,
  taskDaysMap,
  taskCompleted,
  personalTask,
  type CalendarScale,
} from "./calendarModel";
import { CalendarMonth } from "./CalendarMonth";
import { TaskWorkDialog } from "./TaskWorkDialog";
import { AddTaskForDay } from "./AddTaskForDay";

type Panel = "filters" | null;

const EMPTY_TASKS: Task[] = [];
const EMPTY_DAY_MAP = new Map<string, Task[]>();

export const TaskCalendar = () => {
  const { identity } = useGetIdentity();
  const isAdmin = identity?.administrator === true;
  const [installationsOpen, setInstallationsOpen] = useStore<boolean>(
    `calendar.agenda.installations.${identity?.id ?? "guest"}`,
    true,
  );
  const [tasksOpen, setTasksOpen] = useStore<boolean>(
    `calendar.agenda.tasks.${identity?.id ?? "guest"}`,
    true,
  );
  const mobile = useIsMobile();
  const [dayGroups, setDayGroups] = useState<Record<string, boolean>>({});
  const [changing, setChanging] = useState(false);
  const [jump, setJump] = useState(0);
  const [direction, setDirection] = useState(0);
  const [mobileRange, setMobileRange] = useState<{
    scale: CalendarScale;
    start: Date;
    end: Date;
  } | null>(null);
  const navigationTimer = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined,
  );
  useEffect(() => () => clearTimeout(navigationTimer.current), []);
  const navigateCalendar = useCallback(
    (change: () => void) => {
      clearTimeout(navigationTimer.current);
      if (
        !mobile ||
        window.matchMedia("(prefers-reduced-motion: reduce)").matches ||
        document.documentElement.dataset.motion === "off"
      ) {
        setChanging(false);
        change();
        return;
      }
      setChanging(true);
      navigationTimer.current = setTimeout(() => {
        change();
        setChanging(false);
      }, 150);
    },
    [mobile],
  );
  const notify = useNotify();
  const [update] = useUpdate();
  const [scale, setScale] = useStore<CalendarScale>(
    "calendar.scale.v2",
    "month",
  );
  const [dateValue, setDateValue] = useStore(
    "calendar.date.v2",
    format(new Date(), "yyyy-MM-dd"),
  );
  const date = useMemo(() => parseISO(dateValue), [dateValue]);
  const setDate = useCallback(
    (next: Date) => setDateValue(format(next, "yyyy-MM-dd")),
    [setDateValue],
  );
  const [employee, setEmployee] = useState("all");
  const [showTasks, setShowTasks] = useStore("calendar.showTasks", true);
  const [showInstallations, setShowInstallations] = useStore(
    "calendar.showInstallations",
    true,
  );
  const [showCompleted, setShowCompleted] = useStore(
    "calendar.showCompleted",
    true,
  );
  const [panel, setPanel] = useState<Panel>(null);
  const [taskId, setTaskId, taskOpen] = useAnimatedSelection<Identifier>();
  const streamStart =
    mobileRange?.scale === scale
      ? mobileRange.start
      : periodStart(shiftPeriod(date, scale, -streamRadius(scale)), scale);
  const streamEnd =
    mobileRange?.scale === scale
      ? mobileRange.end
      : periodEnd(shiftPeriod(date, scale, streamRadius(scale)), scale);
  const rangeStart = mobile
    ? scale === "month"
      ? startOfWeek(streamStart, { weekStartsOn: 1 })
      : streamStart
    : scale === "year"
      ? startOfYear(date)
      : startOfWeek(startOfMonth(date), { weekStartsOn: 1 });
  const rangeEnd = mobile
    ? scale === "month"
      ? endOfWeek(streamEnd, { weekStartsOn: 1 })
      : streamEnd
    : scale === "year"
      ? endOfYear(date)
      : endOfWeek(endOfMonth(date), { weekStartsOn: 1 });
  const periodFilter = {
    "scheduled_start@lte": rangeEnd.toISOString(),
    "scheduled_end@gt": rangeStart.toISOString(),
  };
  const personal = useCalendarTasks(
    {
      ...periodFilter,
      ...(isAdmin ? {} : { sales_id: identity?.id }),
    },
    mobile,
  );
  const shared = useCalendarTasks({ ...periodFilter, team_task: true }, mobile);
  const installationQuery = useCalendarTasks(
    {
      type: "installation",
      "installation_start@lte": format(rangeEnd, "yyyy-MM-dd"),
      "installation_end@gte": format(rangeStart, "yyyy-MM-dd"),
    },
    mobile,
  );
  const { data: sales = [] } = useGetList<Sale>(
    "sales",
    {
      pagination: { page: 1, perPage: 1000 },
      sort: { field: "first_name", order: "ASC" },
      filter: { "disabled@neq": true },
    },
    { enabled: isAdmin },
  );
  const rawTasks = personal.data ?? EMPTY_TASKS;
  const sharedTasks = shared.data ?? EMPTY_TASKS;
  const rawInstallations = installationQuery.data ?? EMPTY_TASKS;
  const tasks = useMemo(() => {
    const scoped =
      !isAdmin || employee === "all"
        ? rawTasks
        : rawTasks.filter((t) => String(t.sales_id) === employee);
    return [
      ...new Map(
        [...scoped, ...sharedTasks]
          .filter(
            (t) => !isInstallation(t) && (showCompleted || !taskCompleted(t)),
          )
          .map((t) => [String(t.id), t]),
      ).values(),
    ];
  }, [rawTasks, sharedTasks, isAdmin, employee, showCompleted]);
  const personalIds = useMemo(
    () =>
      new Set(
        isAdmin
          ? tasks
              .filter((task) => personalTask(task, identity?.id))
              .map((task) => String(task.id))
          : [],
      ),
    [tasks, isAdmin, identity?.id],
  );
  const projectIds = useMemo(
    () => [
      ...new Set(
        [...tasks, ...rawInstallations].flatMap((task) =>
          task.deal_id == null ? [] : [task.deal_id],
        ),
      ),
    ],
    [tasks, rawInstallations],
  );
  const { data: projects = [], isPending: projectsPending } = useGetMany<{
    id: Identifier;
    name: string;
    calendar_color?: string;
  }>("project_lookup", { ids: projectIds }, { enabled: projectIds.length > 0 });
  const colors = useMemo(
    () =>
      new Map(
        projects.map((project) => [
          String(project.id),
          project.calendar_color ?? "",
        ]),
      ),
    [projects],
  );
  const names = useMemo(
    () =>
      new Map(projects.map((project) => [String(project.id), project.name])),
    [projects],
  );
  const installations = useMemo(
    () =>
      showInstallations
        ? rawInstallations
            .filter((task) => showCompleted || !taskCompleted(task))
            .map((task) => ({
              ...task,
              text: names.has(String(task.deal_id))
                ? `Монтаж «${names.get(String(task.deal_id))}»`
                : task.text,
            }))
        : EMPTY_TASKS,
    [showInstallations, rawInstallations, showCompleted, names],
  );
  const visibleTasks = showTasks ? tasks : EMPTY_TASKS;
  const rangeFirstStamp = rangeStart.toISOString(),
    rangeLastStamp = rangeEnd.toISOString();
  const yearTasks = useMemo(
    () =>
      taskDaysMap(
        visibleTasks,
        new Date(rangeFirstStamp),
        new Date(rangeLastStamp),
      ),
    [visibleTasks, rangeFirstStamp, rangeLastStamp],
  );
  const go = (step: number) => {
    setDirection(step);
    navigateCalendar(() =>
      setDate(
        scale === "year"
          ? addYears(date, step)
          : scale === "month"
            ? addMonths(date, step)
            : addDays(date, step),
      ),
    );
  };
  const openDay = (day: Date) => {
    setDirection(0);
    navigateCalendar(() => {
      setDate(day);
      setScale("day");
    });
  };
  const canMove = (task: Task) =>
    !!identity &&
    (isAdmin ||
      String(task.sales_id) === String(identity.id) ||
      String(task.created_by_sales_id) === String(identity.id));
  const refresh = () => {
    void personal.refetch();
    void shared.refetch();
    void installationQuery.refetch();
  };
  const drop = (event: DragEvent, day: Date) => {
    event.preventDefault();
    event.stopPropagation();
    const task = tasks.find(
      (t) => String(t.id) === event.dataTransfer.getData("text/task-id"),
    );
    if (!task || !canMove(task)) return;
    const oldStart = new Date(
      task.scheduled_start ||
        new Date(new Date(task.due_date).getTime() - 3600000),
    );
    const start = new Date(day);
    start.setHours(oldStart.getHours(), oldStart.getMinutes(), 0, 0);
    const duration = task.scheduled_end
      ? Math.max(
          60000,
          new Date(task.scheduled_end).getTime() - oldStart.getTime(),
        )
      : 3600000;
    const end = new Date(start.getTime() + duration);
    update(
      "tasks",
      {
        id: task.id,
        previousData: task,
        data: {
          scheduled_start: start.toISOString(),
          scheduled_end: end.toISOString(),
        },
      },
      {
        mutationMode: "pessimistic",
        onSuccess: refresh,
        onError: () => notify("Не удалось перенести задачу", { type: "error" }),
      },
    );
  };
  const title =
    scale === "year"
      ? format(date, "yyyy")
      : scale === "day"
        ? format(date, "d MMMM", { locale: ru })
        : format(date, "LLLL yyyy", { locale: ru });
  const busy =
    (projectIds.length > 0 && projectsPending) ||
    personal.isPending ||
    shared.isPending ||
    installationQuery.isPending ||
    (mobile &&
      (personal.isFetching ||
        shared.isFetching ||
        installationQuery.isFetching));
  const error = personal.error || shared.error || installationQuery.error;
  const actionsRef = useRef({ go, openDay, canMove, drop, refresh });
  actionsRef.current = { go, openDay, canMove, drop, refresh };
  const periodActions = useMemo(
    () => ({
      go: (step: number) => actionsRef.current.go(step),
      openDay: (day: Date) => actionsRef.current.openDay(day),
      canMove: (task: Task) => actionsRef.current.canMove(task),
      drop: (event: DragEvent, day: Date) =>
        actionsRef.current.drop(event, day),
      refresh: () => actionsRef.current.refresh(),
    }),
    [],
  );
  const panelTitle = "Настройки календаря";
  const panelContent = (
    <div className="space-y-6">
      {isAdmin && (
        <div className="space-y-3">
          <h3 className="text-sm font-medium">Ответственный</h3>
          <Select value={employee} onValueChange={setEmployee}>
            <SelectTrigger aria-label="Ответственный">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Все сотрудники</SelectItem>
              {sales.map((s) => (
                <SelectItem key={s.id} value={String(s.id)}>
                  {[s.first_name, s.last_name].filter(Boolean).join(" ")}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="text-xs text-muted-foreground">
            Ваши личные задачи отмечены подписью «Личная». Общие задачи и
            монтажи видны при любом фильтре.
          </p>
        </div>
      )}
      <div className="space-y-3">
        <h3 className="text-sm font-medium">События в календаре</h3>
        {[
          ["Задачи со временем", showTasks, setShowTasks],
          ["Монтажи по дням", showInstallations, setShowInstallations],
          ["Выполненные события", showCompleted, setShowCompleted],
        ].map(([label, checked, setter]) => (
          <label
            key={String(label)}
            className="flex cursor-pointer items-center justify-between gap-4 rounded-xl border p-3 text-sm"
          >
            <span>{String(label)}</span>
            <input
              type="checkbox"
              className="size-4 accent-primary"
              checked={!!checked}
              onChange={(e) =>
                (setter as (value: boolean) => void)(e.target.checked)
              }
            />
          </label>
        ))}
        <p className="text-xs text-muted-foreground">
          Цвет — проект. Серый — выполнено.
        </p>
      </div>
      <Button variant="outline" onClick={refresh}>
        <RefreshCw className="size-4" />
        Обновить события
      </Button>
    </div>
  );

  const renderedYearTasks = scale === "year" ? yearTasks : EMPTY_DAY_MAP;
  const periodBusy = scale === "day" && busy;
  const renderPeriod = useCallback(
    (date: Date) => {
      const dateValue = format(date, "yyyy-MM-dd");
      const dayTasks = tasksOnDay(visibleTasks, date);
      const dayKey = format(date, "yyyy-MM-dd");
      const dayInstallations = installations.filter((task) =>
        installationOnDay(task, dateValue),
      );
      return (
        <>
          {scale === "month" && (
            <CalendarMonth
              date={date}
              tasks={visibleTasks}
              installations={installations}
              colors={colors}
              onDay={periodActions.openDay}
              onTask={setTaskId}
              onStep={periodActions.go}
              allowSwipe={!mobile}
              canMove={periodActions.canMove}
              onDrop={periodActions.drop}
              personalIds={personalIds}
            />
          )}
          {scale === "year" && (
            <div
              className="crm-year-grid"
              aria-label={`Календарь на ${format(date, "yyyy")} год`}
            >
              {Array.from({ length: 12 }, (_, month) => {
                const first = new Date(date.getFullYear(), month, 1);
                return (
                  <button
                    type="button"
                    className="crm-mini-month"
                    data-calendar-date={format(first, "yyyy-MM-dd")}
                    key={month}
                    aria-label={`Открыть ${format(first, "LLLL yyyy", { locale: ru })}`}
                    onClick={() =>
                      navigateCalendar(() => {
                        setDate(first);
                        setScale("month");
                      })
                    }
                  >
                    <h3
                      className={
                        month === new Date().getMonth() &&
                        first.getFullYear() === new Date().getFullYear()
                          ? "text-primary"
                          : ""
                      }
                    >
                      {format(first, "LLLL", { locale: ru })}
                    </h3>
                    <div className="crm-mini-days" aria-hidden="true">
                      {["п", "в", "с", "ч", "п", "с", "в"].map((label, i) => (
                        <span
                          className="text-muted-foreground"
                          key={`label-${i}`}
                        >
                          {label}
                        </span>
                      ))}
                      {monthWeeks(first)
                        .flat()
                        .map((day) => {
                          const key = format(day, "yyyy-MM-dd");
                          const entries = renderedYearTasks.get(key) || [];
                          const montages = installations.filter((t) =>
                            installationOnDay(t, key),
                          );
                          const palette = (events: Task[]) => [
                            ...new Set(
                              events.map((t) =>
                                taskCompleted(t)
                                  ? "#888888"
                                  : colors.get(String(t.deal_id)) ||
                                    "var(--primary)",
                              ),
                            ),
                          ];
                          const paint = (values: string[], circle = false) =>
                            values.length === 1
                              ? values[0]
                              : `${circle ? "conic-gradient" : "linear-gradient"}(${circle ? "" : "90deg,"}${values.map((color, i) => `${color} ${(i / values.length) * 100}% ${((i + 1) / values.length) * 100}%`).join(",")})`;
                          return (
                            <span
                              key={key}
                              title={`${format(day, "dd.MM.yyyy")} · Монтажи: ${montages.length} · Задачи: ${entries.length}`}
                              className={`${!isSameMonth(day, first) ? "invisible" : ""} ${isToday(day) ? "crm-mini-today" : ""}`}
                            >
                              {format(day, "d")}
                              {!!entries.length && (
                                <i
                                  className="crm-mini-task-dot"
                                  style={{
                                    background: paint(palette(entries), true),
                                  }}
                                />
                              )}
                              {!!montages.length && (
                                <i
                                  className="crm-mini-montage-line"
                                  style={{
                                    background: paint(palette(montages)),
                                  }}
                                />
                              )}
                            </span>
                          );
                        })}
                    </div>
                  </button>
                );
              })}
            </div>
          )}
          {scale === "day" && (
            <div
              className="crm-calendar-day"
              role="region"
              aria-label={`События на ${format(date, "d MMMM yyyy", { locale: ru })}`}
            >
              <p className="mb-5 text-sm capitalize text-muted-foreground">
                {format(date, "EEEE, yyyy", { locale: ru })}
              </p>
              <AgendaSection
                title="Монтажи"
                regionLabel="Монтажи на весь день"
                count={dayInstallations.length}
                marker={<span className="crm-legend-line" />}
                open={
                  mobile
                    ? (dayGroups[`${dayKey}:installations`] ?? true)
                    : installationsOpen
                }
                onOpenChange={
                  mobile
                    ? (open) =>
                        setDayGroups((previous) => ({
                          ...previous,
                          [`${dayKey}:installations`]: open,
                        }))
                    : setInstallationsOpen
                }
              >
                {dayInstallations.length ? (
                  dayInstallations.map((task) => (
                    <CalendarEvent
                      key={task.id}
                      task={task}
                      day={date}
                      color={colors.get(String(task.deal_id))}
                      onOpen={setTaskId}
                      installation
                    />
                  ))
                ) : (
                  <p className="crm-agenda-empty">
                    {!showInstallations
                      ? "Монтажи скрыты фильтром"
                      : periodBusy
                        ? "Загрузка событий…"
                        : "На этот день монтажей нет"}
                  </p>
                )}
              </AgendaSection>
              <div className="mt-4">
                <AgendaSection
                  title="Задачи"
                  regionLabel="Задачи по времени"
                  count={dayTasks.length}
                  marker={<span className="crm-legend-dot" />}
                  open={
                    mobile ? (dayGroups[`${dayKey}:tasks`] ?? true) : tasksOpen
                  }
                  onOpenChange={
                    mobile
                      ? (open) =>
                          setDayGroups((previous) => ({
                            ...previous,
                            [`${dayKey}:tasks`]: open,
                          }))
                      : setTasksOpen
                  }
                >
                  {dayTasks.length ? (
                    dayTasks.map((task) => (
                      <CalendarEvent
                        key={task.id}
                        task={task}
                        color={colors.get(String(task.deal_id))}
                        day={date}
                        projectName={names.get(String(task.deal_id))}
                        personal={personalIds.has(String(task.id))}
                        owner={
                          task.team_task
                            ? "Вся команда"
                            : sales.find(
                                (s) => String(s.id) === String(task.sales_id),
                              )?.first_name
                        }
                        onOpen={setTaskId}
                      />
                    ))
                  ) : (
                    <p className="crm-agenda-empty">
                      {!showTasks
                        ? "Задачи скрыты фильтром"
                        : periodBusy
                          ? "Загрузка событий…"
                          : "День свободен от задач"}
                    </p>
                  )}
                </AgendaSection>
              </div>
              {!mobile && (
                <div className="mt-5">
                  <AddTaskForDay
                    day={date}
                    salesId={identity?.id}
                    onCreated={periodActions.refresh}
                  />
                </div>
              )}
            </div>
          )}
        </>
      );
    },
    [
      scale,
      visibleTasks,
      installations,
      colors,
      names,
      personalIds,
      renderedYearTasks,
      dayGroups,
      installationsOpen,
      tasksOpen,
      setInstallationsOpen,
      setTasksOpen,
      setTaskId,
      identity?.id,
      sales,
      mobile,
      showTasks,
      showInstallations,
      periodBusy,
      periodActions,
      navigateCalendar,
      setDate,
      setScale,
    ],
  );
  const toolbarActions = (
    <>
      <Button
        variant="ghost"
        size="sm"
        aria-label="Сегодня"
        onClick={() => {
          setDate(new Date());
          if (scale === "year") setScale("month");
        }}
      >
        <span className="hidden sm:inline">Сегодня</span>
        <CalendarDays className="size-4 sm:hidden" />
      </Button>
      <AddTaskForDay
        day={date}
        display="icon"
        salesId={identity?.id}
        onCreated={refresh}
      />
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="icon" aria-label="Меню календаря">
            <MoreHorizontal className="size-5" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem onSelect={() => setPanel("filters")}>
            <SlidersHorizontal className="size-4" />
            Настройки календаря
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem onSelect={refresh}>
            <RefreshCw className="size-4" />
            Обновить
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </>
  );
  return (
    <>
      {mobile && (
        <MobileHeader
          actions={
            <>
              <Button
                variant="ghost"
                size="icon"
                data-mobile-filter
                aria-label="Настройки календаря"
                onClick={() => setPanel("filters")}
              >
                <SlidersHorizontal className="size-4" />
              </Button>
              <MobileCreateAction>
                <AddTaskForDay
                  day={date}
                  display="icon"
                  salesId={identity?.id}
                  onCreated={refresh}
                />
              </MobileCreateAction>{" "}
            </>
          }
        >
          {scale !== "year" && (
            <Button
              variant="ghost"
              size="sm"
              aria-label={
                scale === "month" ? "Вернуться к году" : "Вернуться к месяцу"
              }
              onClick={() =>
                navigateCalendar(() =>
                  setScale(scale === "day" ? "month" : "year"),
                )
              }
            >
              <ChevronLeft className="size-4" />
              <PillLabel>
                {scale === "day"
                  ? format(date, "LLLL", { locale: ru })
                  : format(date, "yyyy")}
              </PillLabel>
            </Button>
          )}
        </MobileHeader>
      )}
      {mobile &&
        createPortal(
          <Button
            data-mobile-today
            variant="outline"
            onClick={() =>
              navigateCalendar(() => {
                setMobileRange(null);
                setDate(new Date());
                setJump((value) => value + 1);
              })
            }
          >
            <PillLabel>Сегодня</PillLabel>
          </Button>,
          document.body,
        )}
      <div className="crm-calendar-toolbar">
        <div className="crm-calendar-heading">
          <CalendarDays className="size-5 text-primary" />
          <div>
            <h1>Календарь</h1>
            <button
              type="button"
              className="crm-calendar-scope"
              onClick={() => setPanel("filters")}
            >
              {isAdmin
                ? "Все доступные задачи и монтажи"
                : "Мои задачи и общие события"}
            </button>
          </div>
        </div>
        <div className="crm-calendar-actions">{!mobile && toolbarActions}</div>
        <div className="crm-calendar-period">
          {!mobile && scale === "day" && (
            <Button
              variant="ghost"
              size="icon"
              aria-label="Вернуться к месяцу"
              onClick={() => setScale("month")}
            >
              <ArrowLeft className="size-4" />
            </Button>
          )}
          <h2 className="capitalize">{title}</h2>
          <div className="crm-calendar-arrows flex items-center">
            <Button
              variant="ghost"
              size="icon"
              aria-label="Предыдущий период"
              onClick={() => go(-1)}
            >
              <ChevronLeft className="size-5" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              aria-label="Следующий период"
              onClick={() => go(1)}
            >
              <ChevronRight className="size-5" />
            </Button>
          </div>
        </div>
        {!mobile && (
          <div className="crm-calendar-scale">
            <SegmentedControl<CalendarScale>
              label="Масштаб календаря"
              value={scale}
              onValueChange={setScale}
              options={[
                { value: "year", label: "Год" },
                { value: "month", label: "Месяц" },
                { value: "day", label: "День" },
              ]}
            />
          </div>
        )}
      </div>
      {error && (
        <div
          role="alert"
          className="shrink-0 border-b px-4 py-2 text-sm text-destructive"
        >
          Не удалось загрузить события.{" "}
          <button type="button" className="underline" onClick={refresh}>
            Повторить
          </button>
        </div>
      )}
      <div
        className="crm-calendar-body"
        aria-busy={busy}
        tabIndex={0}
        aria-label="События календаря"
        data-calendar-body
        onKeyDown={(event) => {
          if (mobile || event.target !== event.currentTarget) return;
          if (event.key === "ArrowLeft" || event.key === "ArrowRight") {
            event.preventDefault();
            go(event.key === "ArrowRight" ? 1 : -1);
          }
        }}
      >
        <div
          key={mobile ? scale : `${scale}-${dateValue}`}
          className="crm-calendar-canvas"
          style={
            {
              "--calendar-direction": direction,
            } as CSSProperties
          }
          data-calendar-view={scale}
          data-changing={changing}
          inert={changing}
        >
          {mobile ? (
            <MobileCalendarStream
              key={`${scale}-${jump}`}
              scale={scale}
              date={date}
              onCurrentDate={setDate}
              onRange={setMobileRange}
              renderPeriod={renderPeriod}
              onZoom={(zoom, anchor) => {
                const levels: CalendarScale[] = ["year", "month", "day"];
                const next =
                  levels[
                    Math.max(0, Math.min(2, levels.indexOf(scale) + zoom))
                  ];
                if (next === scale) return;
                navigateCalendar(() => {
                  if (anchor) setDate(anchor);
                  setScale(next);
                });
              }}
            />
          ) : (
            renderPeriod(date)
          )}
        </div>
        {!mobile && panel && (
          <aside className="crm-calendar-panel" aria-label={panelTitle}>
            <div className="flex items-center justify-between border-b p-4">
              <h2 className="font-semibold">{panelTitle}</h2>
              <Button
                variant="ghost"
                size="icon"
                aria-label="Закрыть меню"
                onClick={() => setPanel(null)}
              >
                <X className="size-4" />
              </Button>
            </div>
            <div className="min-h-0 flex-1 overflow-y-auto p-4">
              {panelContent}
            </div>
          </aside>
        )}
      </div>
      <footer className="crm-calendar-legend">
        <span>
          <span className="crm-legend-line" aria-hidden="true" />
          Монтажи
        </span>
        <span>
          {mobile || scale === "year" ? (
            <i className="crm-legend-dot" />
          ) : (
            <i className="crm-legend-task-card" />
          )}
          Задачи
        </span>
        <span>
          <Check className="size-3" />
          Выполнено
        </span>
        {isAdmin && (
          <span>
            {mobile ? (
              <i className="crm-personal-legend" />
            ) : (
              <strong className="crm-personal-badge">Личная</strong>
            )}
            Личная задача
          </span>
        )}
        {busy && <span className="ml-auto">Загрузка…</span>}
      </footer>
      {mobile && (
        <Sheet
          open={!!panel}
          onOpenChange={(open) => {
            if (!open) setPanel(null);
          }}
        >
          <SheetContent
            side="bottom"
            className="h-dvh flex flex-col"
            aria-describedby={undefined}
          >
            <SheetHeader className="shrink-0 border-b pr-12">
              <SheetTitle>{panelTitle}</SheetTitle>
            </SheetHeader>
            <div className="min-h-0 flex-1 overflow-y-auto p-4 pb-[max(1rem,env(safe-area-inset-bottom))]">
              {panelContent}
            </div>
          </SheetContent>
        </Sheet>
      )}
      {taskId != null && (
        <TaskWorkDialog
          open={taskOpen}
          taskId={taskId}
          onOpenChange={(open) => {
            if (!open) {
              setTaskId(null);
              refresh();
            }
          }}
        />
      )}
    </>
  );
};
